// The Transfer-to-Bank flow behind the Requests page: the modal's open flag
// and rail choice, the confirm call with every 409 answer (needs_onboarding,
// first_cashout_wall, instant_unavailable), the success popup driven by the
// URL, the post-cashout protect snapshot, and the two ways in (?protect=1,
// router state openCashout). Split out of pages/RequestsPage.tsx; the page
// keeps the earnings it fetches with the queue and hands them in.
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { ordersApi } from "../services/orders";
import { paymentsApi } from "../services/payments";
import { stripeApi } from "../services/stripe";
import { usersApi } from "../services/users";
import { cashoutProtectThresholdFor } from "../utils/payinConfig";
import { openStripeUrl, stripeClientFields, onStripeBrowserClosed } from "../lib/stripeFlow";
import { askForAppRating } from "../lib/rateApp";
import { RAIL_STRIPE_INSTANT } from "../components/CashoutRailPicker";
import { normCur, num, type EarningsTotals } from "../utils/requestsFormat";
import { t, formatLocale } from "../i18n";

type Args = {
  earningsByCur: EarningsTotals;
  setEarningsByCur: React.Dispatch<React.SetStateAction<EarningsTotals>>;
  fetchAll: () => Promise<void>;
};

export function useCashout({ earningsByCur, setEarningsByCur, fetchAll }: Args) {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [showCashoutModal, setShowCashoutModal] = useState(false);
  // PROJECT ATLAS — payout destination chosen in the rail picker. Today the
  // server offers exactly one rail (free Stripe bank) so this is informational;
  // POST /payments/cashout stays argument-free until MassPay rails go live.
  const [selectedRailId, setSelectedRailId] = useState<string | null>(null);
  const [feeTipOpen, setFeeTipOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<React.ReactNode>("");
  const [refundFeeTipOpen, setRefundFeeTipOpen] = useState(false);
  // Set when the SERVER refused a transfer as below_minimum (its Stripe-side
  // net is the truth; the client's minLock preview in CashoutFlow is only a
  // preview). Cleared when the modal closes.
  const [serverMinLock, setServerMinLock] = useState<{
    currency: string;
    minimum: number;
    balance: number;
    waiverDays: number;
  } | null>(null);
  const prevEarningsRef = useRef<EarningsTotals>({});

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Cashout success popup is driven by the URL (?justPaidOut=1) instead of
  // component state. Reason: the popup must survive a navigation round-trip
  // (a DJ can leave for Payout History and come back); URL-as-state does.
  const showCashoutSuccessModal = searchParams.get("justPaidOut") === "1";
  // How the money left, for the popup's copy: "instant" (card, ~30 min),
  // "fallback" (asked for instant, got the standard queue), or standard.
  const payoutMode = searchParams.get("payoutMode");
  // The talent's own Stripe delay (from the /cashout response) — "~7 business
  // days", never a hardcoded 2. Older server → 2, as before.
  const payoutDays = Number(searchParams.get("payoutDays")) || 2;
  // "Arrives by Wed, Sep 16" — Stripe's release stamp + the talent's own
  // bank history (server). Day-granular, so it's formatted in UTC.
  const arrivesAtParam = searchParams.get("arrivesAt");
  const arrivesBy = (() => {
    if (!arrivesAtParam) return null;
    const d = new Date(arrivesAtParam);
    return Number.isNaN(d.getTime())
      ? null
      : d.toLocaleDateString(formatLocale() ?? "en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
  })();

  const openCashoutModal = () => setShowCashoutModal(true);
  const closeCashoutModal = () => {
    setShowCashoutModal(false);
    setServerMinLock(null);
  };

  // When the talent isn't Stripe-onboarded yet, the cashout endpoint returns
  // 409 needs_onboarding with the pending amount. We show a dedicated modal
  // that frames it as "Connect your bank to receive $X" instead of an error.
  const [needsOnboardingState, setNeedsOnboardingState] = useState<{
    pendingByCurrency: Record<string, number>;
    ordersCount: number;
  } | null>(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  // MassPay put the talent on a compliance hold: its own document-upload link
  // (server masspay_documents_needed). Shown as a popup with one Upload button.
  const [docsNeededUrl, setDocsNeededUrl] = useState<string | null>(null);

  // Post-cashout track-history protection modal. Snapshotted BEFORE the
  // cashout fires (so payoutBatchId is still null on the orders we care
  // about); rendered AFTER the cashout succeeds if the cashout total in
  // any one currency hit the $100/€100/£100 threshold.
  //
  // Threshold is per-currency, no FX — a $90 + €40 cashout doesn't trigger;
  // a $110 + €40 cashout does. Keeps the modal honest without an FX layer.
  // Phase C: the $100-class threshold comes from payinConfig per currency
  // (1000 kr, 40 000 Ft, …).
  const [protectModalState, setProtectModalState] = useState<{
    orderIds: string[];
    totals: { currency: string; amount: number }[];
    // "wall": the cashout was refused with 409 first_cashout_wall (fraud door)
    // and the panel must collect a validated export before it re-fires.
    mode?: "protect" | "wall";
  } | null>(null);

  // /requests?protect=1 — the laptop link inside the protect modal ("Upload
  // from laptop: Copy link"). The phone showed the modal right after a
  // cashout; on the laptop we rebuild the same modal from the most recent
  // payout batch (the one that just happened), then drop the param so a
  // refresh doesn't reopen it. Best-effort: any miss just shows Requests.
  const protectLinkHandledRef = useRef(false);
  useEffect(() => {
    if (protectLinkHandledRef.current) return;
    if (searchParams.get("protect") !== "1") return;
    if (!isAuthenticated || !user?.slug) return;
    protectLinkHandledRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete("protect");
    setSearchParams(next, { replace: true });
    const slug = user.slug;
    (async () => {
      try {
        const payouts = await usersApi.payouts(slug);
        const batchId = Array.isArray(payouts) ? payouts[0]?.batchId : null;
        if (!batchId) return;
        const orders = await usersApi.payoutOrders(slug, batchId);
        const rows = Array.isArray(orders) ? orders : [];
        if (rows.length === 0) return;
        const byCur: Record<string, number> = {};
        for (const o of rows) {
          const cur = (o.currency || "usd").toUpperCase();
          byCur[cur] = (byCur[cur] || 0) + (typeof o.amount === "number" ? o.amount : 0);
        }
        setProtectModalState({
          orderIds: rows.map((o) => o._id),
          totals: Object.entries(byCur).map(([currency, amount]) => ({ currency, amount })),
        });
      } catch {
        /* older server / nothing paid out yet — the plain Requests page */
      }
    })();
  }, [searchParams, setSearchParams, isAuthenticated, user?.slug]);

  // Dismiss the cashout success popup. Clears the URL flag and — if the
  // cashout was big enough to trigger the track-history protect modal —
  // pops that next. Snapshot lives in sessionStorage so it survives a
  // round-trip via Payout History.
  const dismissCashoutSuccess = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("justPaidOut");
    next.delete("payoutMode");
    next.delete("payoutDays");
    next.delete("arrivesAt");
    setSearchParams(next, { replace: true });

    const stashed = sessionStorage.getItem("pendingProtectSnapshot");
    if (stashed) {
      sessionStorage.removeItem("pendingProtectSnapshot");
      try {
        setProtectModalState(JSON.parse(stashed));
      } catch {
        // Corrupt stash — ignore.
      }
      return;
    }
    // Nothing else to show → the happiest moment in the app: the OS may offer
    // its own rating sheet (native only; never stacked on the protect panel).
    askForAppRating();
  };

  const handleConnectBankFromCashout = async () => {
    setConnectingStripe(true);
    try {
      const res = await stripeApi.connect({ ...stripeClientFields() });
      if (res?.url) {
        // Native: in-app browser sheet (never Safari) — un-stick the button
        // if the talent closes the sheet before the return trip fires.
        const stopBrowserWatch = onStripeBrowserClosed(() => {
          stopBrowserWatch();
          setConnectingStripe(false);
        });
        await openStripeUrl(res.url);
      } else {
        setConnectingStripe(false);
      }
    } catch (err) {
      console.error("Connect Stripe failed:", err);
      setConnectingStripe(false);
    }
  };

  const handleConfirmCashout = async () => {
    setShowCashoutModal(false);

    const prev = earningsByCur;
    prevEarningsRef.current = prev;

    // Snapshot the played orders that are about to be paid out so the
    // post-cashout protection modal has the right orderIds. Must happen
    // BEFORE the cashout fires (the cashout sets payoutBatchId on these
    // orders, which is exactly what we'd filter on). Best-effort — if the
    // fetch fails, we just skip the protect modal; the cashout still goes.
    let protectSnapshot: { orderIds: string[]; totals: { currency: string; amount: number }[] } | null = null;
    try {
      const playedOrders = await ordersApi.played();
      const unpaid = (playedOrders || []).filter((o) => !o.payoutBatchId);
      if (unpaid.length > 0) {
        const byCur: Record<string, number> = {};
        for (const o of unpaid) {
          const cur = (o.currency || "usd").toUpperCase();
          byCur[cur] = (byCur[cur] || 0) + (typeof o.amount === "number" ? o.amount : 0);
        }
        const totals = Object.entries(byCur).map(([currency, amount]) => ({ currency, amount }));
        const hitsThreshold = totals.some(
          ({ currency, amount }) => amount >= cashoutProtectThresholdFor(currency)
        );
        if (hitsThreshold) {
          protectSnapshot = {
            orderIds: unpaid.map((o) => o._id),
            totals,
          };
        }
      }
    } catch {
      // best-effort; protect modal stays closed
    }

    try {
      // Instant Payouts: the picked rail decides the leg. The
      // server answers with what actually happened — instant, or the standard
      // queue with instantFallback when instant couldn't cover the batch.
      const railMethod = selectedRailId === RAIL_STRIPE_INSTANT ? "instant" : "standard";
      const cashoutResult = await paymentsApi.cashout(railMethod);

      // A 200 that sent nothing (2026-10-10): batchId null when there was
      // nothing to cash out, when the balance already rides an earlier
      // transfer still on its way, or when owed refund/dispute fees took all
      // of it (server lib/payouts/cashout.js). Never "Transfer in Progress!"
      // then: one honest line, and the balance read again — it may have moved.
      if (cashoutResult?.batchId === null) {
        setSuccessMessage(t("useCashout.nothingLeftToTransfer"));
        setTimeout(() => setSuccessMessage(""), 4000);
        fetchAll();
        return;
      }

      setEarningsByCur({});

      // Stash the protect snapshot (if any) for after the success popup is
      // dismissed. sessionStorage so it survives a Payout-History round-trip.
      if (protectSnapshot) {
        sessionStorage.setItem(
          "pendingProtectSnapshot",
          JSON.stringify(protectSnapshot)
        );
      }

      // Open the success popup via URL state.
      const next = new URLSearchParams(searchParams);
      next.set("justPaidOut", "1");
      // A MassPay talent's money went to their MassPay wallet (instant), or
      // the answer is still being confirmed with MassPay (202 "preparing").
      const masspayRail = cashoutResult?.rail === "masspay_wallet";
      next.set(
        "payoutMode",
        masspayRail
          ? cashoutResult?.payoutStatus === "preparing" ? "masspay_pending" : "masspay"
          : cashoutResult?.method === "instant" ? "instant" : cashoutResult?.instantFallback ? "fallback" : "standard"
      );
      if (typeof cashoutResult?.payoutDelayDays === "number" && cashoutResult.payoutDelayDays > 0) {
        next.set("payoutDays", String(cashoutResult.payoutDelayDays));
      }
      if (cashoutResult?.arrivalAt) next.set("arrivesAt", cashoutResult.arrivalAt);
      setSearchParams(next, { replace: true });

      setTimeout(() => {
        fetchAll();
      }, 1500);
    } catch (err: any) {
      console.error("Cashout error:", err?.response?.data || err);

      // Server returns 409 + needs_onboarding when the talent has earnings on
      // platform but hasn't connected Stripe yet — flip into the "Connect bank"
      // flow instead of showing a generic error.
      if (err?.response?.status === 409 && err?.response?.data?.msg === "needs_onboarding") {
        setNeedsOnboardingState({
          pendingByCurrency: err.response.data.pendingByCurrency || {},
          ordersCount: err.response.data.ordersCount || 0,
        });
        setEarningsByCur(prevEarningsRef.current || {});
        return;
      }

      // Fraud door: a talent's FIRST cashout at or above the $50-class line
      // needs a validated track-history export. The server answers 409
      // first_cashout_wall with the batch; we open the protect panel in wall
      // mode and it re-fires the cashout by itself once the file is in. Not
      // an error — no "Transfer failed" toast.
      if (err?.response?.status === 409 && err?.response?.data?.msg === "first_cashout_wall") {
        const d = err.response.data || {};
        setEarningsByCur(prevEarningsRef.current || {});
        setProtectModalState({ orderIds: d.orderIds || [], totals: d.totals || [], mode: "wall" });
        return;
      }

      // Instant leg refused before anything moved (rules changed under the
      // modal, card removed, feature switched off): not a money error. Fall
      // back to the bank option and say so — nothing was locked or claimed.
      if (err?.response?.status === 409 && err?.response?.data?.msg === "instant_unavailable") {
        setEarningsByCur(prevEarningsRef.current || {});
        setSelectedRailId("stripe_bank");
        setSuccessMessage(t("useCashout.instantIsnTAvailable"));
        setTimeout(() => setSuccessMessage(""), 4000);
        return;
      }

      // Below the minimum by the server's (Stripe-side) count — reopen the
      // modal in its locked state with the server's numbers, never "failed".
      if (err?.response?.status === 409 && err?.response?.data?.reason === "below_minimum") {
        const d = err.response.data;
        setEarningsByCur(prevEarningsRef.current || {});
        setServerMinLock({
          currency: normCur(d.currency),
          minimum: num(d.minimum),
          balance: num(d.balance),
          waiverDays: typeof d.waiverDays === "number" ? d.waiverDays : 80,
        });
        setShowCashoutModal(true);
        return;
      }

      // MassPay asked for a document (compliance hold): hand the talent
      // MassPay's own upload link. Nothing moved; the balance is untouched.
      const reason = err?.response?.data?.reason;
      if (reason === "masspay_documents_needed" && err?.response?.data?.uploadUrl) {
        setEarningsByCur(prevEarningsRef.current || {});
        setDocsNeededUrl(String(err.response.data.uploadUrl));
        return;
      }

      // Refusals where nothing moved and the money is safe: one honest line.
      //   on_hold            MassPay talent, everything still inside the hold
      //   payouts_paused     our MassPay balance is short (we top it up)
      //   masspay_refused    MassPay said no
      //   cashout_in_progress another transfer (or a MassPay confirmation) is under way
      const unlocks = err?.response?.data?.unlocksAt ? new Date(err.response.data.unlocksAt) : null;
      const line =
        reason === "on_hold" && unlocks && !Number.isNaN(unlocks.getTime())
          ? t("useCashout.yourEarningsUnlockOn", { date: unlocks.toLocaleDateString(formatLocale() ?? "en-US", { month: "short", day: "numeric" }) })
          : reason === "payouts_paused"
            ? t("useCashout.masspayPaused")
            : reason === "masspay_refused"
              ? t("useCashout.masspayRefused")
              : reason === "cashout_in_progress"
                ? t("useCashout.aTransferIsAlready")
                : null;
      if (line) {
        setEarningsByCur(prevEarningsRef.current || {});
        setSuccessMessage(line);
        setTimeout(() => setSuccessMessage(""), 4500);
        return;
      }

      setEarningsByCur(prevEarningsRef.current || {});
      setSuccessMessage(t("useCashout.transferFailedPleaseTry"));
      setTimeout(() => setSuccessMessage(""), 3500);
    }
  };

  // Refer & Earn money alone can be cashed out too (server, 1.5.2).
  const hasFunds = Object.values(earningsByCur).some((v) => num(v?.total) > 0 || num(v?.referral) > 0);

  // Profile's "Transfer to Bank" lands here with router state
  // { openCashout: true } (same pattern as ?justPaidOut=1 above). Wait for
  // funds to load — hasFunds flips after the first /earnings fetch — then
  // open the modal once and clear the state so back/refresh doesn't re-open.
  const location = useLocation();
  useEffect(() => {
    if (!(location.state as any)?.openCashout) return;
    if (!hasFunds) return;
    openCashoutModal();
    navigate(location.pathname + location.search, { replace: true, state: null });
    // Keyed on the navigation state and funds; the path parts and navigate are
    // read at effect time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, hasFunds]);

  return {
    showCashoutModal,
    closeCashoutModal,
    selectedRailId,
    setSelectedRailId,
    feeTipOpen,
    setFeeTipOpen,
    refundFeeTipOpen,
    setRefundFeeTipOpen,
    successMessage,
    showCashoutSuccessModal,
    payoutMode,
    payoutDays,
    arrivesBy,
    dismissCashoutSuccess,
    needsOnboardingState,
    setNeedsOnboardingState,
    connectingStripe,
    handleConnectBankFromCashout,
    protectModalState,
    setProtectModalState,
    handleConfirmCashout,
    hasFunds,
    serverMinLock,
    docsNeededUrl,
    setDocsNeededUrl,
  };
}
